import { memo } from "react";
import Touchable from "src/components/Touchable";
import { MediumText } from "src/components/v1";
import { cn } from "src/utilities/shadcnUtils";

export interface RadioProps {
  checked?: boolean;
  size?: number;
  className?: string;
}

export interface RadioButtonProps<T extends string = string> {
  checked?: boolean;
  onChange?: () => void;
  className?: string;
  label: string;
  // Value is used by parent RadioGroup
  value: T;
}

const STYLES = {
  container: "flex flex-row items-center",
  filled: "bg-green-500 rounded-full",
  wrapper:
    "rounded-full flex items-center justify-center border-solid border-gray-400 border hover:cursor-pointer",
  wrapperChecked: "border-green-500",
  label: "ml-2 text-base",
  touchable: "p-0 items-start justify-start",
};

// make the radio icon independently usable
export const Radio = memo(({ checked = false, size = 16, className }: RadioProps) => {
  const wrapperStyle = { width: size, height: size };
  const fillerStyle = { width: size * 0.75, height: size * 0.75 };
  return (
    <div className={cn(STYLES.wrapper, checked && STYLES.wrapperChecked, className)} style={wrapperStyle}>
      <div
        className={cn(STYLES.filled, "transition-transform duration-200", checked ? "scale-100" : "scale-0")}
        style={fillerStyle}
      />
    </div>
  );
});

const RadioButton = <T extends string = string>({
  checked,
  onChange,
  className,
  label,
}: RadioButtonProps<T>) => (
  <Touchable className={cn(STYLES.touchable, className)} onClick={onChange}>
    <div className={STYLES.container}>
      <Radio checked={checked} />
      <MediumText className={STYLES.label}>{label}</MediumText>
    </div>
  </Touchable>
);

export default memo(RadioButton);
